import React, { useEffect, useState, useRef, ChangeEvent } from 'react';
import { TabComponent, TabItemDirective, TabItemsDirective, SelectingEventArgs, SelectEventArgs } from '@syncfusion/ej2-react-navigations';
import { TreeGridComponent, ColumnsDirective, ColumnDirective } from '@syncfusion/ej2-react-treegrid';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import FacilityStore from './../stores/FacilityStore';
import * as Inovx from '@shared';
import OntologyActions from '../actions/OntologyActions';
import OntologyInformation from './OntologyInformation';
import GraphicContent from './GraphicContent';
import MapConfiguration from './MapConfiguration';
import OperationProgressDialog from './OperationProgressDialog';
import { row, RowDataBoundEventArgs, RowSelectingEventArgs } from '@syncfusion/ej2-react-grids';
import useAsyncActionLock from '@shared/hooks/useAsyncActionLock';

var SystemActions = Inovx.Shared.System.Actions;

var InovxControls = Inovx.Shared.Controls;
var UrlUtilities = Inovx.Shared.Common.UrlUtilities;
var PermissionService = Inovx.Shared.Permission.PermissionService;
var MANAGE_FACILITY_PERMISSION = "Facility.Manage";

declare var InovxLoader: any;

// --- Extended type for TreeView ---
type TreeNode = IFacilityHierarchyPart & {
    icon?: string;
    children?: TreeNode[];
    [key: string]: any; // <-- Add index signature for compatibility
};

const hasManagePermission: boolean = PermissionService.checkPermission(MANAGE_FACILITY_PERMISSION);

const FacilityHierarchyForm: React.FC = () => {
    const [facilityHierarchy, setFacilityHierarchy] = useState<IFacilityHierarchyPart>(FacilityStore.getFacilityHierarchy());
    const [selectedHierarchy, setSelectedHierarchy] = useState<IFacilityHierarchyPart | null>(null);
    const tabInstance = useRef<TabComponent | null>(null);
    const treeGridRef = useRef<TreeGridComponent | null>(null);

    const [isAddChildFormOpen, setIsAddChildFormOpen] = useState(false);
    const [hasAddChildFormError, setHasAddChildFormError] = useState(false);
    const [newChildPartName, setNewChildPartName] = useState("");
    const [activeTab, setActiveTab] = useState(0);
    const [operationMessage, setOperationMessage] = useState<string | null>(null);

    const pendingSelectId = useRef<string | null>(null);
    const addChildParentRef = useRef<IFacilityHierarchyPart | null>(null);

    const [treeDataSource, setTreeDataSource] = useState<TreeNode[]>(null);

    useEffect(() => {
        loadState();

        FacilityStore.addChangeListener(loadState);
        window.addEventListener('beforeunload', checkForUnsavedChanges);

        return () => {
            FacilityStore.removeChangeListener(loadState);
            window.removeEventListener('beforeunload', checkForUnsavedChanges);
        };
    }, []);

    const loadState = () => {
        const updatedFacilityHierarchy = FacilityStore.getFacilityHierarchy();
        setFacilityHierarchy(updatedFacilityHierarchy);

        // Use facility.Hierarchy for the tree data, keep styles and layout unchanged
        const treeData: TreeNode[] = updatedFacilityHierarchy ? [extendHierarchy(updatedFacilityHierarchy)] : [];
        setTreeDataSource(treeData);
    };

    const checkForUnsavedChanges = (event: any) => {
        if (FacilityStore.getOntologyFormDirty()) {
            event.returnValue = InovxLoader.t("common:msgBox.browserSaveChanges");
        }
    };

    function extendHierarchy(member: IFacilityHierarchyPart): TreeNode {
        const icon = 'tree-folder-yellow';
        return {
            ...member,
            icon,
            children: member.Children ? member.Children.map(extendHierarchy) : undefined,
        };
    }

    // Helper to find node by ID
    function findNodeById(node: IFacilityHierarchyPart, id: string): IFacilityHierarchyPart {
        if (!node) return null;
        if (node.ID === id) return node;
        if (node.Children) {
            for (const child of node.Children) {
                const found = findNodeById(child, id);
                if (found) return found;
            }
        }
        return null;
    }

    // Helper to find parent node by child ID
    function findHierarchyNodeParent(node: IFacilityHierarchyPart, childId: string): IFacilityHierarchyPart | null {
        if (!node || !node.Children) return null;
        for (const child of node.Children) {
            if (child.ID === childId) {
                return node;
            }
            const found = findHierarchyNodeParent(child, childId);
            if (found) return found;
        }
        return null;
    }

    function handleAddChildClick(parent: IFacilityHierarchyPart) {
        if (!OntologyActions.verifyOntologyModalChanges()) 
            return;
        if (!parent?.ID) return;

        addChildParentRef.current = parent;
        setNewChildPartName("");
        setHasAddChildFormError(true);
        setIsAddChildFormOpen(true);
    }

    function handleDeleteClick(event?: React.MouseEvent): void {
        if (!OntologyActions.verifyOntologyModalChanges()) 
            return;

        event?.stopPropagation();
        const rowInfo = treeGridRef.current.getRowInfo(event?.target as HTMLImageElement);
        if (rowInfo?.rowData) {
            handleDelete(rowInfo.rowData as IFacilityHierarchyPart);
        }
    }

    const handleDelete = (data: IFacilityHierarchyPart): void => {
        if (data) {
            SystemActions.displayConfirmationMessage({
                message: InovxLoader.t('facility:message.confirmDeleteHierarchyPart'),
                confirmCallback: async function () {
                    setOperationMessage('Deleting ontology.');
                    try {
                        await OntologyActions.deleteOntologyPart(data);
                    } finally {
                        setOperationMessage(null);
                    }
                }
            });
        }
    }

    function closeAddChildForm(): void {
        setIsAddChildFormOpen(false);
        addChildParentRef.current = null;
    }

    function handleAddChildFormInputChange(event: ChangeEvent<HTMLInputElement>): void {
        const { name, value } = event.target;
        if (name === "Name") {
            const trimmedValue = value.trim();
            let hasError = true;
            if (trimmedValue.length > 0) {
                // check if the name is unique among the siblings
                const isUnique = addChildParentRef.current?.Children?.every(
                    child => child.Name.toLocaleLowerCase() !== trimmedValue.toLocaleLowerCase()
                ) ?? true;
                if (isUnique) {
                    hasError = false;
                }
            }
            setHasAddChildFormError(hasError);
            setNewChildPartName(value);
        }
    }

    const isNameUnique = (name: string, id: string) => {
        return selectedHierarchyParent ? selectedHierarchyParent.Children?.every((child) => child.ID === id || child.Name.toLocaleLowerCase() !== name.toLocaleLowerCase()) : true;
    }

    async function createChildHierarchyPart(): Promise<void> {
        const parent = addChildParentRef.current;
        if (!parent?.ID) return;

        setOperationMessage('Creating ontology.');
        try {
            await OntologyActions.createOntologyPart({ Name: newChildPartName, ParentID: parent.ID }, (newPartID) => {
                if (newPartID) {
                    pendingSelectId.current = newPartID;
                }
            });
            closeAddChildForm();
        } finally {
            setOperationMessage(null);
        }
    }

    const createChildAction = useAsyncActionLock(createChildHierarchyPart);

    function handleRowSelecting(e: RowSelectingEventArgs): void {
        if (!e.target) {
            return;
        }

        if (!OntologyActions.verifyOntologyModalChanges()) {
            e.cancel = true; // Prevent selection if there are unsaved changes
            return; 
        }

        let btnElement = (e.target as Element).closest('.add-icon');
        if (btnElement && e.data) {
            setTimeout(() => {
                handleAddChildClick(e.data as IFacilityHierarchyPart);
            }, 10);
        }

        btnElement = (e.target as Element).closest('.delete-icon');
        if (btnElement && e.data) {
            setTimeout(() => {
                handleDelete(e.data as IFacilityHierarchyPart);
            }, 10);
        }
    }

    const handleRowSelected = (e: any): void => {
        const selectedId = e?.data?.ID;
        if (selectedId && facilityHierarchy) {
            const selected = findNodeById(facilityHierarchy, selectedId);
            if (selected) setSelectedHierarchy(selected);
        }
    }

    const handleRowDataBound = (args: RowDataBoundEventArgs) => {
        const row = args.row as HTMLTableRowElement;
        const item = args.data as IFacilityHierarchyPart;

        if (!row || !item) {
            return;
        }

        row.classList.add("cursor-pointer");
    }

    const handleDataBound = (e: any) => {
        let selectRowIndex = 0;
        if (pendingSelectId.current || selectedHierarchy) {
            const records = treeGridRef.current.getCurrentViewRecords() as IFacilityHierarchyPart[];
            
            // Priority to newly created node, otherwise re-select the selected hierarchy
            const selectedId = pendingSelectId.current || selectedHierarchy?.ID;
            const foundRowIndex = records.findIndex((item) => item.ID === selectedId);
            if (foundRowIndex !== -1) {
                selectRowIndex = foundRowIndex;
            }

            pendingSelectId.current = null;
        }
        
        treeGridRef.current.selectRow(selectRowIndex);
    }

    const handleTabSelecting = (e: SelectingEventArgs) => {
        if (!OntologyActions.verifyOntologyModalChanges()) {
            e.cancel = true; // Prevent tab change
        }
    };

    const handleTabSelected = (e: SelectEventArgs) => {
        setActiveTab(e.selectedIndex);
    };

    const selectedHierarchyParent = selectedHierarchy ? findHierarchyNodeParent(facilityHierarchy, selectedHierarchy.ID) : null;
    
    return (
        <div className='facility-hierarchy-form'>
            <InovxControls.StaticHeaderForm title={InovxLoader.t("facility:hierarchy.title")}> 
                <OperationProgressDialog visible={!!operationMessage} message={operationMessage || undefined} />
                <div className="facility-hierarchy-panels">
                    {/* Left Panel: TreeView */}
                    <div className="facility-hierarchy-left-panel">
                        <TreeGridComponent
                            ref={treeGridRef}
                            dataSource={treeDataSource}
                            childMapping="Children"
                            treeColumnIndex={0}
                            height="100%"
                            rowHeight={36}
                            selectionSettings={{ cellSelectionMode: 'Flow', mode: 'Row', type: 'Single', enableToggle: false }}
                            rowSelecting={(e: RowSelectingEventArgs) => handleRowSelecting(e)}
                            rowSelected={(e) => handleRowSelected(e)}
                            rowDataBound={(e) => { handleRowDataBound(e) }}
                            dataBound={(e) => { handleDataBound(e) }}
                            locale={InovxLoader.getSyncfusionJS2Locale() || undefined}
                        >
                            <ColumnsDirective>
                                <ColumnDirective
                                    field="Name"
                                    headerText={InovxLoader.t("facility:hierarchy.nameColumn")}
                                    width="200"
                                    template={(props: any) => (
                                        <div className="facility-hierarchy-treegrid-name">
                                            <div className="facility-hierarchy-treegrid-name-img">
                                                <img
                                                    className="e-treeoverviewimage"
                                                    src={`${UrlUtilities.getBaseApplicationUrl()}/images/icons/folder-yellow.png`}
                                                    alt={InovxLoader.t('facility:form.folderIcon')}
                                                    style={{ width: 22, height: 22, verticalAlign: 'middle' }}
                                                />
                                            </div>
                                            <div className="facility-hierarchy-treegrid-name-label">
                                                {props.Name}
                                            </div>
                                        </div>
                                    )} />
                                <ColumnDirective field="ID" headerText="ID" visible={false} width="0" isPrimaryKey={true} />
                                {hasManagePermission && <ColumnDirective field="Actions" headerText="" width="60" template={(props: any) => {
                                    const isRoot = !props.parentItem;
                                    return (
                                        <span className="facility-hierarchy-actions">
                                            <img src={`${UrlUtilities.getBaseApplicationUrl()}/images/icons/add.png`} className='facility-hierarchy-add-icon add-icon' title={InovxLoader.t('facility:preferences.addButton')} onClick={e => { e.stopPropagation(); handleAddChildClick(props as IFacilityHierarchyPart); }} />
                                            {!isRoot && (
                                                <img src={`${UrlUtilities.getBaseApplicationUrl()}/images/KB/Bin.png`} className='facility-hierarchy-delete-icon delete-icon' title={InovxLoader.t('facility:form.delete')} onClick={e => { handleDeleteClick(e); }} />
                                            )}
                                        </span>
                                    );
                                }} />
                                }
                            </ColumnsDirective>
                        </TreeGridComponent>
                    </div>
                    {/* Right Panel: Tabs */}
                    <div className="facility-hierarchy-right-panel">
                        {/* Using the <TabItemsDirective> for defining tab items causes problems with react updates. Therefore we use an alternative approach
                            described here: https://ej2.syncfusion.com/react/documentation/tab/how-to/react-hooks-tab */}
                        {selectedHierarchy &&
                            <TabComponent
                                heightAdjustMode="None"
                                selectedItem={0}
                                ref={tabInstance}
                                selecting={(e: SelectingEventArgs) => { handleTabSelecting(e); }}
                                selected={(e: SelectEventArgs) => { handleTabSelected(e); }}
                                >
                                <div className="e-tab-header">
                                    <div>{InovxLoader.t("facility:form.ontologyInformation")}</div>
                                    <div>{InovxLoader.t("facility:form.graphicContent")}</div>
                                    <div>{InovxLoader.t("facility:form.mapConfiguration")}</div>
                                </div>
                                <div className="e-content">
                                    <OntologyInformation ontology={selectedHierarchy} isUniqueFunc={isNameUnique} />
                                    <GraphicContent hierarchy={selectedHierarchy} />
                                    <MapConfiguration hierarchy={selectedHierarchy} isTabActive={activeTab === 2} />
                                </div>
                            </TabComponent>
                        }
                    </div>
                </div>
                {isAddChildFormOpen && <DialogComponent
                    target={'#app'}
                    width='350px'
                    showCloseIcon={true}
                    visible={true}
                    isModal={true}
                    zIndex={110000}
                    header={() => (<div><div className="e-icon-settings dlg-template">{InovxLoader.t("facility:form.addChildHierarchyPartTitle")}</div></div>)}
                    footerTemplate={() => {
                        return (<div>
                            <div className="d-flex j-cc">
                                <div>
                                    <a id="facilityApp-save-btn" aria-disabled={createChildAction.isInFlight} onClick={createChildAction.isInFlight ? undefined : () => void createChildAction.run()} className={`btn btn-primary${hasAddChildFormError || createChildAction.isInFlight ? ' disabled' : ''}`}>{InovxLoader.t("facility:preferences.saveButton")}</a>
                                </div>
                                <div className="ml-1">
                                    <a id="facilityApp-cancel-btn" aria-disabled={createChildAction.isInFlight} onClick={createChildAction.isInFlight ? undefined : () => closeAddChildForm()} className={`btn btn-default${createChildAction.isInFlight ? ' disabled' : ''}`}>{InovxLoader.t("facility:preferences.cancelButton")}</a>
                                </div>
                            </div>
                        </div>);
                    }}
                    close={() => closeAddChildForm()}
                >
                    <div className="dialogContent">
                        <InovxControls.FormField title={`${InovxLoader.t("facility:form.hierarchyPartName")}`} inputName='Name' className={hasAddChildFormError ? 'has-error' : ''}>
                            <input type="text" onChange={handleAddChildFormInputChange} value={newChildPartName} className="form-control" name="Name" placeholder={InovxLoader.t("facility:form.hierarchyPartNamePlaceholder")} autoComplete="off" />
                        </InovxControls.FormField>
                    </div>
                </DialogComponent>}
            </InovxControls.StaticHeaderForm>
        </div>
    );
};

export default FacilityHierarchyForm;
